import { useEffect, useState } from 'react';
import { useBulkLocks } from '../../hooks/useBulkLocks';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { ReadOnlyGuard } from '../permissions/ReadOnlyGuard';
import { useAuth } from '../../contexts/AuthContext';
import { useIsPhone } from '../../hooks/useIsPhone';
import {
    Dialog,
    DialogContent,
    DialogTitle,
} from '../ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import { CircleDot, Clock, MapPin, Monitor, User, Phone, Mail, FileText, ArrowLeft, Pencil, CheckCircle2, Globe, CalendarDays, Calendar, Truck, ClipboardList, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';
import { VisiteAnnulerDialog } from './VisiteAnnulerDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const VISITE_STATUS_CONFIG = {
    a_venir: { label: 'A venir', badgeClass: 'bg-blue-100 text-blue-700', borderClass: 'border-blue-400' },
    commencee: { label: 'Commencer', badgeClass: 'bg-indigo-100 text-indigo-700', borderClass: 'border-indigo-400' },
    terminee: { label: 'Terminee', badgeClass: 'bg-emerald-100 text-emerald-700', borderClass: 'border-emerald-400' },
    decalee: { label: 'A reprogrammer', badgeClass: 'bg-orange-100 text-orange-700', borderClass: 'border-orange-400' },
    annulee: { label: 'Annulee', badgeClass: 'bg-red-100 text-red-700', borderClass: 'border-red-400' },
};

const VISITE_TRANSITIONS = {
    a_venir: ['commencee', 'decalee', 'annulee'],
    commencee: ['terminee'],
    terminee: ['decalee'],
    decalee: ['a_venir', 'annulee'],
    annulee: [],
};

// Transitions reservees a l'administrateur (par-dessus VISITE_TRANSITIONS)
const VISITE_TRANSITIONS_ADMIN = {
    annulee: ['a_venir'],
};

function formatDateFr(dateStr) {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return format(d, 'dd MMMM yyyy', { locale: fr });
    } catch { return dateStr; }
}

const SectionLine = ({ isComplete }) => (
    <div className={`w-1 rounded-full self-stretch shrink-0 ${isComplete ? 'bg-emerald-500' : 'bg-red-400'}`} />
);

const SectionCheck = ({ isComplete }) => (
    isComplete ? <CheckCircle2 size={16} className="text-emerald-500 shrink-0" /> : null
);

function VisiteAnnulationInfo({ visiteId }) {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        let cancelled = false;
        axios.get(`${API}/api/visites/${visiteId}/annulation-info`, { withCredentials: true })
            .then(({ data }) => { if (!cancelled) setInfo(data); })
            .catch(() => {});
        return () => { cancelled = true; };
    }, [visiteId]);

    if (!info) return null;
    if (!info.remarque && !info.has_file) return null;

    return (
        <div className="mt-4 border-l-4 border-red-300 bg-red-50/40 rounded-r px-4 py-3 space-y-2" data-testid="visite-annulation-info">
            {info.remarque && (
                <div>
                    <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">Motif d'annulation</p>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{info.remarque}</p>
                </div>
            )}
            {info.has_file && (
                <div className="flex items-center gap-2">
                    <FileText size={14} className="text-red-600 shrink-0" />
                    <a
                        href={`${API}/api/visites/${visiteId}/justificatif`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-sm font-medium text-[#2e7cd6] hover:underline truncate"
                        data-testid="visite-annulation-justificatif-link"
                    >
                        {info.filename || 'Voir le justificatif'}
                    </a>
                </div>
            )}
        </div>
    );
}

export function VisiteDetailContent({ visite, onClose, onEdit, onRefresh, config, onReprogrammerRequest, onReplanifierRequest }) {
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === 'admin';
    const isPhone = useIsPhone();
    // phoneReadOnly retire : tous les utilisateurs avec droits d'ecriture
    // (admin, commercial, assistante) peuvent modifier sur tous les appareils
    // (telephone, tablette, PC). Le ReadOnlyGuard ci-dessous gere deja les
    // droits via la permission "visites_calendrier".
    const phoneReadOnly = false;
    const [creatingInventaire, setCreatingInventaire] = useState(false);
    const [showAnnulerDialog, setShowAnnulerDialog] = useState(false);
    const visiteLocks = useBulkLocks('visite', visite?.id ? [visite.id] : [], { enabled: !!visite?.id });
    const lockedByOther = visite?.id ? visiteLocks[visite.id] : null;
    if (!visite) return null;

    const statusCfg = VISITE_STATUS_CONFIG[visite.statut] || VISITE_STATUS_CONFIG.a_venir;

    // Liste des transitions accessibles depuis le statut courant.
    // L'admin dispose de transitions supplementaires (ex: annulee -> a_venir).
    const baseTransitions = VISITE_TRANSITIONS[visite.statut] || [];
    const adminTransitions = isAdmin ? (VISITE_TRANSITIONS_ADMIN[visite.statut] || []) : [];
    const availableTransitions = [...baseTransitions, ...adminTransitions];
    const canChangeStatus = availableTransitions.length > 0;

    const getAscenseurLabel = (val) => {
        if (!val) return '-';
        const opt = config?.ascenseur_options?.find(a => a.value === val);
        return opt ? opt.label : val;
    };

    const handleStatusChange = async (newStatus) => {
        if (newStatus === 'decalee' && onReprogrammerRequest) {
            onReprogrammerRequest(visite);
            return;
        }
        // Annulation : ouvrir la modale qui exige remarque + justificatif
        if (newStatus === 'annulee') {
            setShowAnnulerDialog(true);
            return;
        }
        // Reactivation d'une visite annulee (admin uniquement) : confirmation
        // explicite avant de remettre le statut a "A venir". Les donnees
        // d'annulation (motif/justificatif) sont effacees cote backend.
        if (visite.statut === 'annulee' && newStatus === 'a_venir') {
            if (!isAdmin) {
                toast.error("Seul l'administrateur peut reactiver une visite annulee.");
                return;
            }
            const ok = window.confirm(
                "Reactiver cette visite annulee ?\n\nLe motif d'annulation et le justificatif seront effaces, et la visite repassera au statut \"A venir\"."
            );
            if (!ok) return;
        }
        // Quand on passe de "À reprogrammer" à "Visite" (a_venir) → ouvrir le
        // formulaire d'édition complet (mêmes champs que la création initiale)
        // pour saisir la nouvelle date/heure et déclencher la confirmation email.
        if (visite.statut === 'decalee' && newStatus === 'a_venir') {
            if (onReplanifierRequest) {
                onReplanifierRequest(visite);
            } else if (onEdit) {
                onEdit();
            }
            return;
        }
        try {
            const { data: updated } = await axios.put(
                `${API}/api/visites/${visite.id}`,
                { statut: newStatus },
                { withCredentials: true }
            );
            toast.success('Statut visite mis à jour');
            // Synchronise le state local immediatement avec la reponse du serveur.
            // Sur tablette en particulier, le refresh de la liste peut arriver
            // apres avec un statut "stale" (race). Le GET direct ci-apres
            // garantit qu'on a bien la valeur finale.
            if (updated && typeof updated === 'object') {
                visite.statut = updated.statut || newStatus;
            }
            // Petit delai pour laisser le serveur stabiliser, puis re-fetch
            setTimeout(() => { if (onRefresh) onRefresh(); }, 200);
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        }
    };

    const hasDate = !!visite.date && !!visite.heure;
    const hasType = !!visite.type_visite;
    const hasInfos = !!visite.prospect_nom;
    const adresseDepart = visite.prospect_adresse_depart;
    const adresseArrivee = visite.prospect_adresse_arrivee;

    const isAdresseComplete = (a) => {
        return a && !!a.date && !!a.adresse && !!a.etage && !!a.ascenseur && !!a.portage && a.autorisation_stationnement !== null && a.monte_meuble !== null;
    };

    const AddressBlock = ({ title, address, icon: Icon, isComplete, sectionNum }) => {
        if (!address || !address.adresse) {
            return (
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Icon size={16} className="text-[#2e7cd6]" />
                            {sectionNum}. {title}
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={false} />
                            <p className="text-sm text-slate-400 italic">Non renseigné</p>
                        </div>
                    </CardContent>
                </Card>
            );
        }
        return (
            <Card className="border-slate-200">
                <CardHeader className="pb-2 border-b border-slate-100">
                    <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                        <Icon size={16} className="text-[#2e7cd6]" />
                        {sectionNum}. {title}
                        <SectionCheck isComplete={isComplete} />
                    </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                    <div className="flex gap-4">
                        <SectionLine isComplete={isComplete} />
                        <div className="flex-1 divide-y divide-slate-100 text-sm">
                            {address.date && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Date</p>
                                    <p className="flex items-center gap-2 text-sm font-medium text-slate-900">
                                        <Calendar size={14} className="text-slate-400" />
                                        {formatDateFr(address.date)}
                                    </p>
                                </div>
                            )}
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Adresse</p>
                                <p className="text-sm font-medium text-slate-900">{address.adresse}</p>
                            </div>
                            {address.etage && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Étage</p>
                                    <p className="text-sm font-medium text-slate-900">{address.etage}</p>
                                </div>
                            )}
                            {address.portage && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Portage</p>
                                    <p className="text-sm font-medium text-slate-900">{address.portage}</p>
                                </div>
                            )}
                            {address.ascenseur && (
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Ascenseur</p>
                                    <p className="text-sm font-medium text-slate-900">{getAscenseurLabel(address.ascenseur)}</p>
                                </div>
                            )}
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Autorisation stationnement</p>
                                <p className="text-sm font-medium text-slate-900">
                                    {address.autorisation_stationnement === true ? 'Oui' : address.autorisation_stationnement === false ? 'Non' : '-'}
                                </p>
                            </div>
                            <div className="flex items-center gap-4 py-3">
                                <p className="text-slate-500 w-[140px] md:w-[460px] shrink-0">Monte-meuble</p>
                                <p className="text-sm font-medium text-slate-900">
                                    {address.monte_meuble === true ? 'Oui' : address.monte_meuble === false ? 'Non' : '-'}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>
        );
    };

    return (
        <>
            {/* Sticky Header — reste TOUJOURS cliquable (hors ReadOnlyGuard) */}
            <div className="sticky top-0 z-50 bg-white border-b px-4 md:px-6 py-3 md:py-4">
                <div className="flex items-center justify-between gap-2">
                    <Button variant="outline" size="sm" onClick={onClose} data-testid="visite-detail-retour-btn">
                        <ArrowLeft size={16} className="mr-1 md:mr-2" />
                        Retour
                    </Button>
                    <div className="flex items-center gap-2">
                        {!phoneReadOnly && visite.statut !== 'decalee' && visite.statut !== 'annulee' && (
                            <Button
                                variant="outline"
                                size="sm"
                                className={visite.statut === 'a_venir' ? 'bg-blue-50 text-blue-300 border-blue-200 cursor-not-allowed' : 'bg-[#2e7cd6] hover:bg-[#2568b8] text-white'}
                                disabled={creatingInventaire || visite.statut === 'a_venir'}
                                onClick={visite.statut === 'a_venir' ? () => toast.info('Commence d\'abord ta visite') : async () => {
                                    setCreatingInventaire(true);
                                    try {
                                        const { data: check } = await axios.get(`${API}/api/inventaires/check/${visite.prospect_id}`, { withCredentials: true });
                                        if (check.exists) {
                                            onClose();
                                            navigate('/inventaires', { state: { open_inventaire_id: check.inventaire_id } });
                                            return;
                                        }
                                        const typeInv = visite.type_visite === 'virtuelle' ? 'visite_virtuelle' : 'visite_presentielle';
                                        const { data: newInv } = await axios.post(`${API}/api/inventaires`, {
                                            prospect_id: visite.prospect_id,
                                            type_inventaire: typeInv,
                                            commercial_id: visite.commercial_id || null,
                                            visite_id: visite.id
                                        }, { withCredentials: true });
                                        toast.success('Inventaire cree');
                                        onClose();
                                        navigate('/inventaires', { state: { open_inventaire_id: newInv.id } });
                                    } catch (err) {
                                        toast.error(err.response?.data?.detail || 'Erreur lors de la creation');
                                    } finally { setCreatingInventaire(false); }
                                }}
                                data-testid="visite-detail-inventaire-btn"
                            >
                                {creatingInventaire ? <Loader2 size={16} className="mr-1 animate-spin" /> : <ClipboardList size={16} className="mr-1 md:mr-2" />}
                                Inventaire
                            </Button>
                        )}
                        {!phoneReadOnly && (
                            <Button variant="outline" size="sm" onClick={onEdit} disabled={!!lockedByOther} title={lockedByOther ? `${lockedByOther.user_name} est en train de modifier cette fiche` : ''} data-testid="visite-detail-edit-btn">
                                <Pencil size={16} className="mr-1 md:mr-2" />
                                Modifier
                            </Button>
                        )}
                    </div>
                </div>
                <h2 className="text-base md:text-2xl font-semibold leading-tight tracking-tight mt-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    Visite {visite.prospect_nom || 'Client'}, {formatDateFr(visite.date)}, {visite.heure}
                </h2>
                {visite.date_initiale && visite.date_initiale !== visite.date && (
                    <p className="text-xs text-orange-600 mt-1 font-medium" data-testid="visite-date-initiale-note">
                        Initialement prévue le {formatDateFr(visite.date_initiale)}
                    </p>
                )}
            </div>

            <ReadOnlyGuard module="visites_calendrier" forceReadOnly={phoneReadOnly}>
            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto px-4 md:px-6 py-4">
                <div className="space-y-6">

                {/* Section 1: Statut de la visite */}
                <Card className={`bg-blue-50 border-2 ${statusCfg.borderClass}`}>
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-blue-800">
                            <CircleDot size={16} />
                            1. Statut de la visite
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="flex items-center gap-4">
                            {!canChangeStatus ? (
                                <Badge variant="outline" className={`${statusCfg.badgeClass} border text-sm px-3 py-1`}>{statusCfg.label}</Badge>
                            ) : (
                                <>
                                    <Select value={visite.statut} onValueChange={handleStatusChange}>
                                        <SelectTrigger className="w-[250px] bg-white" data-testid="visite-status-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {[visite.statut, ...availableTransitions].map(key => {
                                                const cfg = VISITE_STATUS_CONFIG[key];
                                                if (!cfg) return null;
                                                // Quand on est en "À reprogrammer", l'option "À venir" est libellée "Nouvelle date"
                                                const displayLabel = (visite.statut === 'decalee' && key === 'a_venir') ? 'Nouvelle date' : cfg.label;
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        <span className={`inline-block px-2 py-0.5 rounded text-sm ${cfg.badgeClass}`}>{displayLabel}</span>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    <Badge variant="outline" className={`${statusCfg.badgeClass} border text-sm px-3 py-1`}>{statusCfg.label}</Badge>
                                </>
                            )}
                        </div>
                        {visite.statut === 'annulee' && <VisiteAnnulationInfo visiteId={visite.id} />}
                    </CardContent>
                </Card>

                {/* Section 2: Type de visite et Commercial */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            {visite.type_visite === 'virtuelle' ? <Monitor size={16} className="text-[#2e7cd6]" /> : <MapPin size={16} className="text-[#2e7cd6]" />}
                            2. Type de visite et Commercial
                            <SectionCheck isComplete={hasType} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={hasType} />
                            <div className="flex-1 divide-y divide-slate-100">
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Type</p>
                                    <Badge variant="outline" className={visite.type_visite === 'virtuelle' ? 'bg-violet-100 text-violet-700 border-violet-200' : 'bg-cyan-100 text-cyan-700 border-cyan-200'}>
                                        {visite.type_visite === 'virtuelle' ? 'Virtuelle' : 'Présentielle'}
                                    </Badge>
                                </div>
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Commercial</p>
                                    {visite.commercial_name ? (
                                        <Badge
                                            variant="outline"
                                            className="border text-sm px-3 py-1"
                                            style={visite.commercial_couleur ? {
                                                backgroundColor: visite.commercial_couleur + '1A',
                                                color: visite.commercial_couleur,
                                                borderColor: visite.commercial_couleur + '40'
                                            } : {}}
                                            data-testid="visite-commercial-badge"
                                        >
                                            {visite.commercial_couleur && (
                                                <span className="w-2.5 h-2.5 rounded-full mr-2 inline-block" style={{ backgroundColor: visite.commercial_couleur }} />
                                            )}
                                            {visite.commercial_name}
                                        </Badge>
                                    ) : (
                                        <Badge variant="outline" className="bg-slate-100 text-slate-500 border-slate-200 text-sm px-3 py-1">
                                            Non affecté
                                        </Badge>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 3: Date et heure */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <CalendarDays size={16} className="text-[#2e7cd6]" />
                            3. Date et heure
                            <SectionCheck isComplete={hasDate} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={hasDate} />
                            <div className="flex-1 divide-y divide-slate-100">
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Date</p>
                                    <p className="text-sm font-medium text-slate-900">{formatDateFr(visite.date)}</p>
                                </div>
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Heure</p>
                                    <p className="text-sm font-medium text-slate-900 flex items-center gap-2">
                                        <Clock size={14} className="text-[#2e7cd6]" />
                                        {visite.heure}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 4: Informations client */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <User size={16} className="text-[#2e7cd6]" />
                            4. Informations client
                            <SectionCheck isComplete={hasInfos} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={hasInfos} />
                            <div className="flex-1 divide-y divide-slate-100">
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Nom</p>
                                    <p className="text-sm font-medium text-slate-900">{visite.prospect_nom || '-'}</p>
                                </div>
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Téléphone</p>
                                    {visite.prospect_telephone ? (
                                        <p className="flex items-center gap-2 text-sm text-slate-900"><Phone size={14} className="text-[#2e7cd6]" />{visite.prospect_telephone}</p>
                                    ) : (
                                        <p className="text-slate-400 italic text-sm">Non renseigné</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Email</p>
                                    {visite.prospect_email ? (
                                        <p className="flex items-center gap-2 text-sm text-slate-900"><Mail size={14} className="text-[#2e7cd6]" />{visite.prospect_email}</p>
                                    ) : (
                                        <p className="text-slate-400 italic text-sm">Non renseigné</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 5: Source & Département */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <Globe size={16} className="text-[#2e7cd6]" />
                            5. Source & Département
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={!!visite.prospect_source} />
                            <div className="flex-1 divide-y divide-slate-100">
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Source</p>
                                    <p className="text-sm font-medium text-slate-900">
                                        {visite.prospect_source || <span className="text-slate-400 italic">Non renseigné</span>}
                                    </p>
                                </div>
                                <div className="flex items-center gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0">Département</p>
                                    <p className="text-sm font-medium text-slate-900">
                                        {visite.prospect_departement || <span className="text-slate-400 italic">Non renseigné</span>}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Section 6: Adresse de départ */}
                <AddressBlock title="Adresse de départ" address={adresseDepart} icon={MapPin} isComplete={isAdresseComplete(adresseDepart)} sectionNum={6} />

                {/* Section 7: Adresse d'arrivée */}
                <AddressBlock title="Adresse d'arrivée" address={adresseArrivee} icon={Truck} isComplete={isAdresseComplete(adresseArrivee)} sectionNum={7} />

                {/* Section 8: Remarques */}
                <Card className="border-slate-200">
                    <CardHeader className="pb-2 border-b border-slate-100">
                        <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
                            <FileText size={16} className="text-[#2e7cd6]" />
                            8. Remarques
                            <SectionCheck isComplete={!!visite.prospect_remarques} />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        <div className="flex gap-4">
                            <SectionLine isComplete={!!visite.prospect_remarques} />
                            <div className="flex-1">
                                <div className="flex items-start gap-4 py-3">
                                    <p className="text-sm text-slate-500 w-[140px] md:w-[460px] shrink-0 pt-1">Remarques</p>
                                    {visite.prospect_remarques ? (
                                        <p className="text-sm text-slate-900 whitespace-pre-wrap">{visite.prospect_remarques}</p>
                                    ) : (
                                        <p className="text-slate-400 italic">Aucune remarque</p>
                                    )}
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

                </div>
            </div>

            <VisiteAnnulerDialog
                open={showAnnulerDialog}
                onOpenChange={setShowAnnulerDialog}
                visite={visite}
                onSuccess={() => { if (onRefresh) onRefresh(); }}
            />
            </ReadOnlyGuard>
        </>
    );
}

export function VisiteDetailDialog({ open, onOpenChange, visite, onEdit, onRefresh, config }) {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                className="max-w-5xl w-full max-h-[90vh] flex flex-col p-0"
                onPointerDownOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
                hideCloseButton
                data-testid="visite-detail-dialog"
            >
                <VisiteDetailContent
                    visite={visite}
                    onClose={() => onOpenChange(false)}
                    onEdit={onEdit}
                    onRefresh={onRefresh}
                    config={config}
                />
            </DialogContent>
        </Dialog>
    );
}
